import { View, StyleSheet } from "react-native";
import { useState, useEffect } from "react";
import DefaultLayout from "../layouts/DefaultLayout";
import CustomText from "../components/common/general/CustomText/CustomText.js";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { UnChecked, Checked } from "../components/svgs/common";
import { useGetTenantSchemaQuery, useGetTenantQuery, GetTenantDocument } from "../graphql/generated/graphql";
import { ScrollView } from "react-native-gesture-handler";
import { useNavigation, useRoute } from "@react-navigation/native";
import CustomTextInput from "../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import Loader from "../components/common/status/Loader/Loader";
import Divider from "../components/common/layout/Divider/Divider";
import CustomModal from "../components/common/general/CustomModal/CustomModal.js";
import { useUpdateTenantMutation } from "../graphql/generated/graphql";
import SamlConfig from "../containers/tenantDashboard/SamlConfig";
import DynamicFormContainer from "../containers/tenantDashboard/DynamicFormContainer";

const deepEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => deepEqual(v, b[i]));
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }
  return false;
};

export default function TenantSettingsDashboardView() {
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params;

  const [tenantSchema, setTenantSchema] = useState(null);
  const [visible, setVisible] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [values, setValues] = useState({});

  const { data: schemaData } = useGetTenantSchemaQuery();
  const { data: tenantData } = useGetTenantQuery({ variables: { id: params.id } });

  const [updateTenant, { error: updateTenantError }] = useUpdateTenantMutation({
    refetchQueries: [
      {
        query: GetTenantDocument,
        variables: { id: params.id },
      },
    ],
  });

  useEffect(() => {
    if (schemaData?.getTenantSchema?.data) {
      setTenantSchema(JSON.parse(schemaData.getTenantSchema.data));
    }
  }, [schemaData]);

  useEffect(() => {
    if (tenantData?.getTenant?.data?.[0]) {
      const initial = tenantData.getTenant.data[0];
      setOriginalValues(initial);
      setValues(initial);
    }
  }, [tenantData]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const openModal = () => setVisible(true);
  const closeModal = () => setVisible(false);

  const handleGoBack = () => {
    navigation.navigate("Tenant Dashboard");
  };

  const handleSubmit = async () => {
    const diff = Object.keys(values).reduce((acc, key) => {
      if (!deepEqual(originalValues[key], values[key])) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    try {
      const { data } = await updateTenant({ variables: { id: params.id, tenant: diff } });
      if (data?.updateTenant?.success) {
        closeModal();
        navigation.navigate("Tenant Dashboard");
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!tenantSchema || !tenantData) {
    return <Loader />;
  }

  const changedKeys = Object.keys(values).filter((key) => !deepEqual(originalValues[key], values[key]));

  return (
    <DefaultLayout pageHasChat={false}>
      <CustomButton
        text="← Back to Tenant Dashboard"
        onPress={handleGoBack}
        useTranslationText={false}
        styleType="transparent"
        size="s"
        style={styles.backButton}
      />
      <CustomSpacing size="m" />
      <CustomText
        role="header"
        text="Tenant Settings"
        size="l"
        textType="display"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="xxl" />
      <CustomSpacing size="xxl" />
      <SamlConfig tenantId={params.id} tenantDomain={values.tenantDomain} />
      <CustomSpacing size="xxl" />
      <CustomSpacing size="xxl" />
      <CustomText
        role="header"
        text={`${values.tenantDomain} settings - general form`}
        size="xxl"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="l" />

      <CustomSpacing size="l" />

      <ScrollView contentContainerStyle={styles.formContainer}>
        <DynamicFormContainer
          schema={tenantSchema.definitions.Tenant}
          originalValues={originalValues}
          values={values}
          onValueChange={handleChange}
          onSavePressed={openModal}
          saveButtonText="Save General Form Changes"
          saveButtonStyleType="lightGreen"
        />
      </ScrollView>

      <CustomModal
        visible={visible}
        style={styles.modal}
        onRequestClose={closeModal}
        showCloseButton
        title="Confirm Changes">
        <CustomText role="header" text="You are about to update:" size="m" weight="medium" useTranslationText={false} />
        <Divider size="m" />
        <ScrollView style={styles.modalList}>
          {changedKeys.map((key) => (
            <View key={key} style={styles.modalItem}>
              <CustomText role="header" text={key} size="m" weight="medium" useTranslationText={false} />
              <CustomText
                role="header"
                text={JSON.stringify(values[key])}
                size="m"
                style={{ color: "#d9534f" }}
                useTranslationText={false}
              />
              <Divider size="s" />
            </View>
          ))}
        </ScrollView>
        {updateTenantError && (
          <>
            <CustomText
              role="header"
              text="Error updating tenant. Please retry."
              size="m"
              style={{ color: "#d9534f" }}
            />
            <Divider size="m" />
          </>
        )}
        <CustomButton text="Confirm" styleType="lightGreen" onPress={handleSubmit} />
        <CustomSpacing size="m" />
        <CustomButton text="Cancel" styleType="danger" onPress={closeModal} />
      </CustomModal>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  backButton: {
    alignSelf: "flex-start",
  },
  formContainer: {
    paddingBottom: 40,
  },
  modal: {
    width: "90%",
    height: "70%",
  },
  modalList: {
    maxHeight: 200,
    marginVertical: 8,
  },
  modalItem: {
    marginBottom: 8,
  },
});
